import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPost } from "../services/api";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/login.css";

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const roleRouteMap = {
  SuperAdmin: "/Dashboard",
  Admin: "/Dashboard",
  TaskCoordinator: "/Dashboard",
  Viewer: "/Dashboard",
};

const OTP_EXPIRY_MINUTES = 15;
const MAX_OTP_ATTEMPTS = 3;
const RESEND_COOLDOWN_SECONDS = 60;

const speak = (text, rate = 0.9) => {
  const synth = window.speechSynthesis;
  const voices = synth.getVoices();
  const femaleVoice =
    voices.find(v =>
      v.lang.toLowerCase().includes("en") &&
      (v.name.toLowerCase().includes("zira") ||
        v.name.toLowerCase().includes("anya") ||
        v.name.toLowerCase().includes("female") ||
        v.name.toLowerCase().includes("susan"))
    ) || voices.find(v => v.lang === "en-US");

  const utter = new SpeechSynthesisUtterance(text);
  utter.voice = femaleVoice;
  utter.lang = "en-US";
  utter.rate = rate;
  synth.speak(utter);
};

const LoginPage = ({ setAuth }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [listeningField, setListeningField] = useState(null);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(0);
  const [forgotEmail, setForgotEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [remainingTime, setRemainingTime] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [passwordValid, setPasswordValid] = useState(null);
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] = useState(MAX_OTP_ATTEMPTS);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState("");
  const [otpResendDisabled, setOtpResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(RESEND_COOLDOWN_SECONDS);
  const navigate = useNavigate();

  useEffect(() => {
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);
  
  useEffect(() => {
  const token = sessionStorage.getItem("token");
  if (!token) {
    setUsername("");
    setPassword("");
    setEmailValid(null);
    setPasswordValid(null);
  }
}, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token) {
      navigate("/dashboard");
    } else {
      const timer = setTimeout(() => {
        speak("Please enter your email ID and password.", 0.85);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  useEffect(() => {
    if (!otpExpiry) return;
    
    const timer = setInterval(() => {
      const now = new Date();
      const diffMs = otpExpiry - now;
      
      if (diffMs <= 0) {
        clearInterval(timer);
        setRemainingTime("Expired");
        if (forgotPasswordStep === 2) {
          setErrorMsg("OTP has expired. Please request a new one.");
          speak("OTP has expired. Please request a new one.");
        }
        return;
      }
      
      const diffMins = Math.floor(diffMs / 60000);
      const diffSecs = Math.floor((diffMs % 60000) / 1000);
      
      const formattedTime = `${diffMins}:${diffSecs < 10 ? '0' : ''}${diffSecs}`;
      setRemainingTime(formattedTime);
      
    }, 1000);
    
    return () => clearInterval(timer);
  }, [otpExpiry, forgotPasswordStep]);

  useEffect(() => {
    if (otpResendDisabled && resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0) {
      setOtpResendDisabled(false);
      setResendCountdown(RESEND_COOLDOWN_SECONDS);
    }
  }, [otpResendDisabled, resendCountdown]);

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());

  const sanitizeEmail = (input) => {
  let processed = input
    .toLowerCase()
    .replace(/\s*(at the rate|at the rate of|at|add|et|ad|and|hashtag|rate|aab|aaj)\s*/gi, "@")
    .replace(/\s*(dot|dt|dott|dhot|dought|point|doht|dout|doh|do|daught|dog|duh)\s*/gi, ".")
    .replace(/\s*(underscore|under score|under|score|under score|under the score)\s*/gi, "_")
    .replace(/\s+/g, "")
    .replace(/\.+$/, "");
  const namePreservationPatterns = [
    { pattern: /santosh/gi, replacement: 'santosh' },
    { pattern: /nidhi/gi, replacement: 'nidhi' },
    { pattern: /gunjan/gi, replacement: 'gunjan' },
    { pattern: /harshgarg/gi, replacement: 'harshgarg' }
  ];


  for (const { pattern, replacement } of namePreservationPatterns) {
    processed = processed.replace(pattern, replacement);
  }


  processed = processed.replace(/(^|\s)one(\s|$)/gi, '$11$2')
    .replace(/(^|\s)two(\s|$)/gi, '$12$2')
    .replace(/(^|\s)three(\s|$)/gi, '$13$2')
    .replace(/(^|\s)four(\s|$)/gi, '$14$2')
    .replace(/(^|\s)five(\s|$)/gi, '$15$2')
    .replace(/(^|\s)six(\s|$)/gi, '$16$2')
    .replace(/(^|\s)seven(\s|$)/gi, '$17$2')
    .replace(/(^|\s)eight(\s|$)/gi, '$18$2')
    .replace(/(^|\s)nine(\s|$)/gi, '$19$2')
    .replace(/(^|\s)zero(\s|$)/gi, '$10$2');

  
  processed = processed.replace(/anj\s*(dot|dt|dott|dhot|dought|point|doht|dout|doh|do)\s*xyz/gi, 'anj.xyz')
    .replace(/gmail\s*(dot|dt|dott|dhot|dought|point|doht|dout|doh|do)\s*com/gi, 'gmail.com')
    .replace(/yahoo\s*(dot|dt|dott|dhot|dought|point|doht|dout|doh|do)\s*com/gi, 'yahoo.com')
    .replace(/outlook\s*(dot|dt|dott|dhot|dought|point|doht|dout|doh|do)\s*com/gi, 'outlook.com')
    .replace(/hotmail\s*(dot|dt|dott|dhot|dought|point|doht|dout|doh|do)\s*com/gi, 'hotmail.com');


  const commonEmails = {
    'nidhi1@anj.xyz': /nidhi\s*(one|1)\s*(at|@)\s*anj\s*(dot|\.)\s*xyz/gi,
    'gunjan@gmail.com': /gunjan\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi,
    'viewer@gmail.com': /viewer\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi,
    'admin@gmail.com': /admin\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi,
    'tc2@gmail.com': /tc\s*(two|2)\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi,
    'tc@gmail.com': /tc\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi,
    'santosh@gmail.com': /santosh\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi,
    'harshgarg5786@gmail.com': /harsh\s*garg\s*(five|5)\s*(seven|7)\s*(eight|8)\s*(six|6)\s*(at|@)\s*gmail\s*(dot|\.)\s*com/gi
  };
  for (const [email, regex] of Object.entries(commonEmails)) {
    if (regex.test(processed)) {
      return email;
    }
  }
  processed = processed.replace(/\s+/g, '');
  const atCount = (processed.match(/@/g) || []).length;
  if (atCount > 1) {
    processed = processed.replace(/@+/, '@');
  } else if (atCount === 0 && processed.includes('.')) {
    const lastDotIndex = processed.lastIndexOf('.');
    processed = processed.slice(0, lastDotIndex) + '@' + processed.slice(lastDotIndex + 1);
  }

  return processed;
};


  const sanitizePassword = (input) => {
    return input.replace(/\D/g, "").slice(0, 7);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    setEmailValid(value === "" ? null : isValidEmail(value));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordValid(value === "" ? null : value.length >= 5);
  };

  const handleSpeechInput = (field) => {
    if (!SpeechRecognition) {
      alert("Speech Recognition is not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setListeningField(field);
    recognition.onerror = () => setListeningField(null);
    recognition.onend = () => setListeningField(null);

    recognition.onresult = (event) => {
      let transcript = event.results[0][0].transcript.trim();
      console.log("Raw speech transcript:", transcript);
      if (field === "email" || field === "forgotEmail") {
        transcript = sanitizeEmail(transcript);
      } else if (field === "password" || field === "otp") {
        transcript = sanitizePassword(transcript);
      }

      console.log("Processed transcript:", transcript);

      switch (field) {
        case "email":
          setUsername(transcript);
          setEmailValid(isValidEmail(transcript));
          break;
        case "password":
          setPassword(transcript);
          setPasswordValid(transcript.length >= 5);
          break;
        case "forgotEmail":
          setForgotEmail(transcript);
          break;
        case "otp":
          setOtp(transcript);
          break;
      }
    };

    recognition.start();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!username && !password) {
      const msg = "Please enter your email ID and password.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }
    if (!username) {
      const msg = "Please enter your email ID.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }
    if (!isValidEmail(username)) {
      const msg = "This is not a valid email. Try again.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }
    if (!password) {
      const msg = "Password field cannot be empty.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }
    if (password.length < 5) {
      const msg = "Password must be at least 5 characters.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }

    setLoading(true);

    try {
      const userData = await apiPost("/auth/login", { username, password });
      const { token, roleName } = userData;

      if (!token || !roleName) {
        throw new Error("Invalid login response");
      }

      sessionStorage.setItem("token", token);
      sessionStorage.setItem("user", JSON.stringify(userData));
      if (setAuth) setAuth(true);

      const route = roleRouteMap[roleName];
      if (route) {
        speak("Welcome to your dashboard");
        navigate(route);
      } else {
        const msg = `No dashboard found for your role: ${roleName}`;
        speak(msg);
        alert(msg);
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.data?.message ||
        error?.message ||
        "Login failed";
      setErrorMsg(message);
      setPasswordValid(false);
      speak(`Oops! ${message}`, 0.95);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!forgotEmail) {
      const msg = "Please enter your email.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }
    if (!isValidEmail(forgotEmail)) {
      const msg = "Entered email is not valid.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }

    setLoading(true);
    try {
      const response = await apiPost("/auth/ForgotPassword", { 
        username: forgotEmail 
      });
      
      if (response?.Success || response?.success) {
        setOtpSent(true);
        setForgotPasswordStep(2);
        const expiryTime = new Date();
        expiryTime.setMinutes(expiryTime.getMinutes() + OTP_EXPIRY_MINUTES);
        setOtpExpiry(expiryTime);
        setOtpAttemptsRemaining(response?.AttemptsRemaining || MAX_OTP_ATTEMPTS - 1);
        setOtpResendDisabled(true);
        setErrorMsg("");
        speak(`OTP has been sent to your email. Please check and enter it here. This OTP will expire in ${OTP_EXPIRY_MINUTES} minutes.`);
      } else if (response?.IsBlocked) {
        setIsUserBlocked(true);
        setErrorMsg(response.Message);
        setBlockTimeRemaining(response.BlockTimeRemaining);
        speak(response.Message);
      } else {
        const errorMsg = response?.Message || response?.message || "Failed to send OTP";
        setErrorMsg(errorMsg);
        speak(errorMsg);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 
                      error?.message || 
                      "An error occurred while sending OTP";
      setErrorMsg(errorMsg);
      speak(errorMsg);
      console.error("Error sending OTP:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpResendDisabled(true);
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
    setErrorMsg("");
    
    try {
      const response = await apiPost("/auth/ForgotPassword", { 
        username: forgotEmail 
      });
      
      if (response?.Success || response?.success) {
        const expiryTime = new Date();
        expiryTime.setMinutes(expiryTime.getMinutes() + OTP_EXPIRY_MINUTES);
        setOtpExpiry(expiryTime);
        setOtpAttemptsRemaining(response?.AttemptsRemaining || MAX_OTP_ATTEMPTS - 1);
        setErrorMsg("");
        speak(`New OTP has been sent to your email. Please check and enter it here.`);
      } else if (response?.IsBlocked) {
        setIsUserBlocked(true);
        setErrorMsg(response.Message);
        setBlockTimeRemaining(response.BlockTimeRemaining);
        speak(response.Message);
      } else {
        const errorMsg = response?.Message || response?.message || "Failed to resend OTP";
        setErrorMsg(errorMsg);
        speak(errorMsg);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || 
                      error?.message || 
                      "An error occurred while resending OTP";
      setErrorMsg(errorMsg);
      speak(errorMsg);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!otp || otp.length !== 6) {
      const msg = "Please enter a valid 6-digit OTP.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }

    if (remainingTime === "Expired") {
      const msg = "OTP has expired. Please request a new one.";
      setErrorMsg(msg);
      speak(msg);
      return;
    }

    setLoading(true);
    try {
      const response = await apiPost("/auth/VerifyOtpAndSendPassword", {
        usernameOrEmail: forgotEmail,
        enteredOtp: otp
      });

      if (response?.Success || response?.success) {
        setShowSuccessPopup(true);
        speak("Password has been sent to your registered email address.");

        setTimeout(() => {
          setShowSuccessPopup(false);
          setForgotPasswordStep(0);
          setUsername(forgotEmail);
          setForgotEmail("");
          setOtp("");
          setOtpSent(false);
          setOtpExpiry(null);
          setRemainingTime("");
          setOtpAttemptsRemaining(MAX_OTP_ATTEMPTS);
          setIsUserBlocked(false);
          setOtpResendDisabled(false);
          setResendCountdown(RESEND_COOLDOWN_SECONDS);
        }, 3000);
      } else if (response?.IsBlocked) {
        setIsUserBlocked(true);
        setErrorMsg(response.Message);
        setBlockTimeRemaining(response.BlockTimeRemaining);
        speak(response.Message);
      } else {
        const errorMsg = response?.Message || response?.message || "Invalid OTP. Please try again.";
        setErrorMsg(errorMsg);
        speak(errorMsg);
        setOtpAttemptsRemaining(response?.AttemptsRemaining || otpAttemptsRemaining - 1);
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "OTP verification failed.";
      setErrorMsg(errorMsg);
      speak(errorMsg);
      console.error("OTP verification error:", error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Helper function to get input class with color indicators
  const getInputClass = (validationState) => {
    if (validationState === null) return 'form-control';
    return validationState ? 'form-control border-success' : 'form-control border-danger';
  };

  const renderForgotPasswordForm = () => {
    switch (forgotPasswordStep) {
      case 1: 
        return (
          <form onSubmit={handleSendOtp} noValidate>
            <h5 className="text-center mb-3">Reset Password</h5>
            
            {errorMsg && (
              <div className={`alert ${isUserBlocked ? 'alert-danger' : 'alert-warning'} fw-bold text-center`}>
                {errorMsg}
                {isUserBlocked && blockTimeRemaining && (
                  <div>Time remaining: {blockTimeRemaining}</div>
                )}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="forgotEmail" className="form-label">Email</label>
              <div className="input-group">
                <input
                  id="forgotEmail"
                  type="email"
                  className={getInputClass(forgotEmail ? isValidEmail(forgotEmail) : null)}
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onFocus={() => speak("Enter your email.", 0.9)}
                  autoComplete="username"
                  required
                  disabled={isUserBlocked}
                  style={{
                    borderWidth: '1px',
                    borderColor: forgotEmail ? (isValidEmail(forgotEmail) ? '#04364A' : '#dc3545') : '#ced4da'
                  }}
                />
                <button
                  type="button"
                  className={`loginmicro-button ${listeningField === "forgotEmail" ? "active" : ""}`}
                  onClick={() => handleSpeechInput("forgotEmail")}
                  disabled={isUserBlocked}
                >
                  <i className={`bi bi-mic custom-microphone${listeningField === "forgotEmail" ? "-fill" : ""}`}></i>
                </button>
              </div>
              {forgotEmail && !isValidEmail(forgotEmail) && (
                <div className="text-danger small mt-1">Please enter a valid email address</div>
              )}
            </div>

            <div className="d-flex  mt-3">
              
                 <button 
                type="submit" 
                className="btn submit-btn"
                disabled={loading || isUserBlocked || !forgotEmail || !isValidEmail(forgotEmail)}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
              <button
                type="button"
                className="back-btn"
                onClick={() => {
                  setForgotPasswordStep(0);
                  setErrorMsg("");
                  setIsUserBlocked(false);
                }}
              >
              <i class="bi bi-arrow-left-short back-arrow"></i>   Back
              </button>
           
            </div>
          </form>
        );

      case 2: 
        return (
          <form onSubmit={handleVerifyOtp} noValidate>
            <h5 className="text-center mb-3">Enter OTP</h5>
            
            {errorMsg && (
              <div className={`alert ${isUserBlocked ? 'alert-danger' : 'alert-warning'} fw-bold text-center`}>
                {errorMsg}
                {isUserBlocked && blockTimeRemaining && (
                  <div>Time remaining: {blockTimeRemaining}</div>
                )}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="otp" className="form-label">
                6-digit OTP {otpExpiry && (
                  <span className={`small ${remainingTime === "Expired" ? "text-danger" : "text-muted"}`}>
                    (Expires in: {remainingTime})
                  </span>
                )}
                {otpAttemptsRemaining >= 0 && (
                  <span className="small text-muted ms-2">
                    Attempts left: {otpAttemptsRemaining}
                  </span>
                )}
              </label>
              <div className="input-group">
                <input
                  id="otp"
                  type="text"
                  className={getInputClass(otp ? (otp.length === 6) : null)}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onFocus={() => speak("Enter the 6-digit OTP sent to your email.", 0.9)}
                  inputMode="numeric"
                  maxLength={6}
                  required
                  disabled={remainingTime === "Expired" || isUserBlocked}
                  style={{
                    borderWidth: '1px',
                    borderColor: otp ? (otp.length === 6 ? '#04364A' : '#dc3545') : '#ced4da'
                  }}
                />
                <button
                  type="button"
                  className={`loginmicro-button${listeningField === "otp" ? "active" : ""}`}
                  onClick={() => handleSpeechInput("otp")}
                  disabled={remainingTime === "Expired" || isUserBlocked}
                >
                  <i className={`bi bi-mic custom-microphone${listeningField === "otp" ? "-fill" : ""}`}></i>
                </button>
              </div>
              {otp && otp.length !== 6 && (
                <div className="text-danger small mt-1">OTP must be 6 digits</div>
              )}
              <div className="form-text">
                Check your email for the OTP. It's valid for {OTP_EXPIRY_MINUTES} minutes.
                <button 
                  type="button" 
                  className="btn btn-link p-0 ms-2"
                  onClick={handleResendOtp}
                  disabled={otpResendDisabled || isUserBlocked || remainingTime === "Expired"}
                >
                  Resend OTP {otpResendDisabled && `(${resendCountdown}s)`}
                </button>
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setForgotPasswordStep(1);
                  setOtp("");
                  setOtpSent(false);
                  setErrorMsg("");
                  setIsUserBlocked(false);
                  setOtpResendDisabled(false);
                  setResendCountdown(RESEND_COOLDOWN_SECONDS);
                }}
                disabled={isUserBlocked}
              >
                Back
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading || remainingTime === "Expired" || otp.length !== 6 || isUserBlocked}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Verifying...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </div>
          </form>
        );

      default: 
        return (
          <form onSubmit={handleLogin} noValidate>
            {errorMsg && (
              <div className="alert alert-warning fw-bold text-center">
                {errorMsg}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email</label>
              <div className="input-group">
                <input
                  id="email"
                  type="email"
                  className={getInputClass(emailValid)}
                  value={username}
                  onChange={handleEmailChange}
                  onFocus={() => speak("Enter your email ID.", 0.9)}
                  autoComplete="username"
                  required
                  style={{
                    borderWidth: '1px',
                    borderColor: emailValid !== null ? (emailValid ? '#04364A' : '#dc3545') : '#ced4da'
                  }}
                />
                <button
                  type="button"
                  className={`loginmicro-button${listeningField === "email" ? "active" : ""}`}
                  onClick={() => handleSpeechInput("email")}
                >
                  <i className={`bi bi-mic custom-microphone${listeningField === "email" ? "-fill" : ""}`}></i>
                </button>
              </div>
              {emailValid === false && (
                <div className="text-danger small mt-2">Please enter a valid email address</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="input-group">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className={getInputClass(passwordValid)}
                  value={password}
                  onChange={handlePasswordChange}
                  onFocus={() => speak("Enter your password.", 0.9)}
                  autoComplete="current-password"
                  required
                  style={{
                    borderWidth: '1px',
                    borderColor: passwordValid !== null ? (passwordValid ? '#04364A' : '#dc3545') : '#ced4da'
                  }}
                />
                <div className="input-group-append">
                  <button
                    type="button"
                    className={`loginmicro-button${listeningField === "password" ? "active" : ""}`}
                    onClick={() => handleSpeechInput("password")}
                  >
                    <i className={`bi bi-mic custom-microphone${listeningField === "password" ? "-fill" : ""}`}></i>
                  </button>
                  <button
                    type="button"
                    className="loginmicro-button2"
                    onClick={togglePasswordVisibility}
                  >
                    <i className={`bi bi-eye-slash custom-microphone${showPassword ? "-slash" : ""}`}></i>
                  </button>
                </div>
              </div>
              {passwordValid === false && (
                <div className="text-danger small mt-2">Password must be at least 5 characters</div>
              )}
            </div>

            <button
              type="submit"
              className="btn submit-btn w-100 mb-2  mt-2"
              disabled={loading || !emailValid || !passwordValid}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </button>

            <button
              type="button"
              className="forgot-password-text  w-100"
              onClick={() => {
                setForgotPasswordStep(1);
                setErrorMsg("");
              }}
            >
              Forgot Password?
            </button>
          </form>
        );
    }
  };

  return (
    <div className="login-page">
      {showSuccessPopup && (
        <div className="success-popup">
          <div className="success-popup-content">
            <div className="alert alert-success">
              <p>Your password has been sent to your registered email address.</p>
            </div>
          </div>
        </div>
      )}

      <div className="login-box">
        <div className="illustration-wrapper col-md-7">
          <img
            src={process.env.PUBLIC_URL + "/login.jpg"}
            alt="Login Illustration"
            className="img-fluid"
          />
        </div>

        <div className="p-4 col-md-5">
          <h3 className="text-center mb-3 animate-charcter">ePARINAM</h3>
          {forgotPasswordStep === 0 && (
            <p className="text-muted loginmute text-center">
              Enabling Performance through Accountable and Rigorous Instructions & Authority Monitoring
            </p>
          )}

          {renderForgotPasswordForm()}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;